import { useCallback, useEffect, useRef } from "react";

import { parseOnboardingAnswerRemote } from "../../../api/onboardingApi";
import { OTHER_INDUSTRY } from "../constants";
import { STEP_INDUSTRY, STEP_LOCATION, STEP_REVENUE } from "../onboardingShared";
import { buildIndustryAnswer, buildLocationAnswer, buildRevenueAnswer } from "./parseAnswer";

/**
 * Typed onboarding answers are interpreted by the backend LLM parser (owner
 * ruling 2026-10-09), like Chat. Chip picks stay local. Any backend failure
 * degrades to the local parser so onboarding never blocks.
 */

const REMOTE_STEPS = {
  [STEP_INDUSTRY]: "industry",
  [STEP_LOCATION]: "location",
  [STEP_REVENUE]: "revenue",
};

const MILLION = 1_000_000;

const cleanLabels = (labels) =>
  Array.isArray(labels)
    ? labels.filter((label) => typeof label === "string" && label.trim())
    : [];

const unique = (labels) => Array.from(new Set(labels));

/**
 * Industry and location typed text always goes to the backend (local-first
 * misreads "New York City" as STATE: New York); revenue only on a local miss.
 */
export const shouldParseRemotely = (parseStep, typedText, localParsed) => {
  const text = String(typedText || "").trim();
  if (!text || !REMOTE_STEPS[parseStep]) return false;
  if (parseStep === STEP_REVENUE) return !localParsed?.ok;
  if (parseStep === STEP_INDUSTRY && text.toLowerCase() === OTHER_INDUSTRY.toLowerCase()) {
    return false;
  }
  return true;
};

const toMillions = (value) => {
  if (value == null) return { ok: true, amount: null };
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return { ok: false };
  return { ok: true, amount: value * MILLION };
};

const toRevenueAnswer = (revenue) => {
  if (!revenue || typeof revenue !== "object") return null;
  const lower = toMillions(revenue.lower_millions);
  const upper = toMillions(revenue.upper_millions);
  if (!lower.ok || !upper.ok) return null;
  if (lower.amount != null && upper.amount != null && lower.amount > upper.amount) return null;
  return buildRevenueAnswer(lower.amount, upper.amount);
};

/**
 * Convert a "parsed" backend payload into the local parser's {ok, value,
 * display} shape, merged with any chips already picked. Null when unusable.
 */
const withRemoteDisplay = (parsed, data, extraLabels = []) => {
  const display = typeof data?.display === "string" ? data.display.trim() : "";
  if (!parsed || !display) return parsed;
  // The backend may resolve one phrase to several filter values ("New York
  // City" -> five borough counties); its display is what the user meant.
  return { ...parsed, display: [...extraLabels, display].join(", ") };
};

export const toParsedAnswer = (parseStep, data, chips = []) => {
  if (parseStep === STEP_REVENUE) return withRemoteDisplay(toRevenueAnswer(data?.revenue), data);
  if (parseStep === STEP_INDUSTRY || parseStep === STEP_LOCATION) {
    const remote = cleanLabels(parseStep === STEP_INDUSTRY ? data?.industries : data?.locations);
    if (remote.length === 0) return null;
    const chipLabels = cleanLabels(chips).filter((label) => !remote.includes(label));
    const labels = unique([...chipLabels, ...remote]);
    const parsed = parseStep === STEP_INDUSTRY ? buildIndustryAnswer(labels) : buildLocationAnswer(labels);
    return withRemoteDisplay(parsed, data, unique(chipLabels));
  }
  return null;
};

export function useRemoteAnswerParser({ parseRemote = parseOnboardingAnswerRemote } = {}) {
  const mountedRef = useRef(true);
  const pendingRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const isPending = useCallback(() => pendingRef.current, []);

  /**
   * Resolves to {parsed, clarification}, or null when another request is in
   * flight or the component unmounted (the caller must then do nothing).
   */
  const resolveAnswer = useCallback(
    async ({ parseStep, text, chips = [], localParsed }) => {
      if (pendingRef.current) return null;
      pendingRef.current = true;
      let data = null;
      try {
        data = await parseRemote(REMOTE_STEPS[parseStep], text);
      } catch (error) {
        data = null;
      } finally {
        pendingRef.current = false;
      }
      if (!mountedRef.current) return null;

      if (data?.status === "clarify") {
        const clarification = typeof data.clarification === "string" ? data.clarification.trim() : "";
        return { parsed: { ok: false }, clarification };
      }
      const remoteParsed = data?.status === "parsed" ? toParsedAnswer(parseStep, data, chips) : null;
      return { parsed: remoteParsed || localParsed, clarification: "" };
    },
    [parseRemote]
  );

  return { isPending, resolveAnswer };
}
